import React, { useState, useEffect } from 'react';
import api from '../../api/axios';

const emptyForm = { name: '', description: '' };

const ProcedureTypes = () => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expandedType, setExpandedType] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [checklistForm, setChecklistForm] = useState({ name: '', description: '', required: true, order: 0 });
  const [showChecklistForm, setShowChecklistForm] = useState(null);
  const [editingChecklist, setEditingChecklist] = useState(null);

  const fetchTypes = async () => {
    try {
      const res = await api.get('/procedure-types');
      setTypes(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTypes(); }, []);

  const handleNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const handleEdit = (type) => {
    setEditing(type);
    setForm({ name: type.name, description: type.description || '' });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditing(null);
    setForm(emptyForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editing) {
        await api.put(`/procedure-types/${editing.id}`, form);
      } else {
        await api.post('/procedure-types', form);
      }
      handleCancel();
      fetchTypes();
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Desactivar este tipo de trámite?')) return;
    await api.delete(`/procedure-types/${id}`);
    fetchTypes();
  };

  // Checklist
  const handleNewChecklist = (typeId) => {
    setEditingChecklist(null);
    setChecklistForm({ name: '', description: '', required: true, order: 0 });
    setShowChecklistForm(typeId);
  };

  const handleEditChecklist = (item, typeId) => {
    setEditingChecklist(item);
    setChecklistForm({ name: item.name, description: item.description || '', required: item.required, order: item.order });
    setShowChecklistForm(typeId);
  };

  const handleCancelChecklist = () => {
    setShowChecklistForm(null);
    setEditingChecklist(null);
    setChecklistForm({ name: '', description: '', required: true, order: 0 });
  };

  const handleSubmitChecklist = async (e, typeId) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingChecklist) {
        await api.put(`/procedure-types/${typeId}/document-checklists/${editingChecklist.id}`, checklistForm);
      } else {
        await api.post(`/procedure-types/${typeId}/document-checklists`, checklistForm);
      }
      handleCancelChecklist();
      fetchTypes();
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteChecklist = async (typeId, checklistId) => {
    if (!window.confirm('¿Eliminar este documento del checklist?')) return;
    await api.delete(`/procedure-types/${typeId}/document-checklists/${checklistId}`);
    fetchTypes();
  };

  return (
    <div className="container-fluid">
      <div className="page-header d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">Tipos de Trámite</h4>
        <button className="btn btn-primary" onClick={handleNew}>
          <i className="ti-plus mr-2"></i> Nuevo Tipo
        </button>
      </div>

      {showForm && (
        <div className="card mb-4">
          <div className="card-header">
            <h6 className="mb-0">{editing ? 'Editar Tipo de Trámite' : 'Nuevo Tipo de Trámite'}</h6>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-5">
                  <div className="form-group">
                    <label>Nombre <span className="text-danger">*</span></label>
                    <input type="text" className="form-control" value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                  </div>
                </div>
                <div className="col-md-7">
                  <div className="form-group">
                    <label>Descripción</label>
                    <input type="text" className="form-control" value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })} />
                  </div>
                </div>
              </div>
              <button type="submit" className="btn btn-primary btn-sm mr-2" disabled={submitting}>
                {submitting ? 'Guardando...' : 'Guardar'}
              </button>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleCancel}>Cancelar</button>
            </form>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-4"><div className="spinner-border text-primary"></div></div>
          ) : types.length === 0 ? (
            <div className="text-center text-muted py-5">No hay tipos de trámite registrados</div>
          ) : (
            types.map((type) => (
              <div key={type.id} className="border-bottom">
                <div className="d-flex justify-content-between align-items-center px-4 py-3">
                  <div>
                    <strong>{type.name}</strong>
                    {type.description && <small className="text-muted ml-2">{type.description}</small>}
                    <span className="badge badge-light ml-2">{type.document_checklists?.length || 0} documentos</span>
                  </div>
                  <div>
                    <button className="btn btn-sm btn-outline-info mr-1"
                      onClick={() => setExpandedType(expandedType === type.id ? null : type.id)}>
                      <i className={`ti-${expandedType === type.id ? 'minus' : 'plus'}`}></i> Checklist
                    </button>
                    <button className="btn btn-sm btn-outline-secondary mr-1" onClick={() => handleEdit(type)}>
                      <i className="ti-pencil"></i>
                    </button>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(type.id)}>
                      <i className="ti-trash"></i>
                    </button>
                  </div>
                </div>

                {expandedType === type.id && (
                  <div className="px-4 pb-3 bg-light">
                    <div className="d-flex justify-content-between align-items-center py-2">
                      <small className="text-muted font-weight-bold">DOCUMENTOS DEL CHECKLIST</small>
                      <button className="btn btn-sm btn-primary" onClick={() => handleNewChecklist(type.id)}>
                        <i className="ti-plus mr-1"></i> Agregar Documento
                      </button>
                    </div>

                    {showChecklistForm === type.id && (
                      <div className="border rounded p-3 mb-3 bg-white">
                        <form onSubmit={(e) => handleSubmitChecklist(e, type.id)}>
                          <div className="row">
                            <div className="col-md-5">
                              <div className="form-group">
                                <label>Nombre del documento <span className="text-danger">*</span></label>
                                <input type="text" className="form-control" value={checklistForm.name}
                                  onChange={(e) => setChecklistForm({ ...checklistForm, name: e.target.value })} required />
                              </div>
                            </div>
                            <div className="col-md-5">
                              <div className="form-group">
                                <label>Descripción</label>
                                <input type="text" className="form-control" value={checklistForm.description}
                                  onChange={(e) => setChecklistForm({ ...checklistForm, description: e.target.value })} />
                              </div>
                            </div>
                            <div className="col-md-2">
                              <div className="form-group">
                                <label>Orden</label>
                                <input type="number" className="form-control" value={checklistForm.order}
                                  onChange={(e) => setChecklistForm({ ...checklistForm, order: parseInt(e.target.value) })} />
                              </div>
                            </div>
                            <div className="col-md-12">
                              <div className="custom-control custom-checkbox mb-3">
                                <input type="checkbox" className="custom-control-input" id={`req-${type.id}`}
                                  checked={checklistForm.required}
                                  onChange={(e) => setChecklistForm({ ...checklistForm, required: e.target.checked })} />
                                <label className="custom-control-label" htmlFor={`req-${type.id}`}>Documento obligatorio</label>
                              </div>
                            </div>
                          </div>
                          <button type="submit" className="btn btn-primary btn-sm mr-2" disabled={submitting}>
                            {submitting ? 'Guardando...' : 'Guardar'}
                          </button>
                          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleCancelChecklist}>Cancelar</button>
                        </form>
                      </div>
                    )}

                    {type.document_checklists?.length === 0 ? (
                      <p className="text-muted small">No hay documentos en el checklist</p>
                    ) : (
                      <table className="table table-sm bg-white rounded">
                        <thead>
                          <tr>
                            <th>Documento</th>
                            <th>Descripción</th>
                            <th>Obligatorio</th>
                            <th>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {type.document_checklists?.map((item) => (
                            <tr key={item.id}>
                              <td>{item.name}</td>
                              <td>{item.description || '-'}</td>
                              <td>
                                {item.required
                                  ? <span className="badge badge-danger">Sí</span>
                                  : <span className="badge badge-light">No</span>}
                              </td>
                              <td>
                                <button className="btn btn-sm btn-outline-secondary mr-1"
                                  onClick={() => handleEditChecklist(item, type.id)}>
                                  <i className="ti-pencil"></i>
                                </button>
                                <button className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleDeleteChecklist(type.id, item.id)}>
                                  <i className="ti-trash"></i>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ProcedureTypes;
